/**
 * Booking pricing breakdown (stay ex-caution, caution adjustments, total).
 * Matches API {@see App\Support\BookingCautionFee::breakdown} when pricing_breakdown is absent (e.g. owner list).
 */

export function getPricingBreakdown(booking) {
  if (booking?.pricing_breakdown && typeof booking.pricing_breakdown === 'object') {
    return booking.pricing_breakdown;
  }
  const s = Number(booking?.stay_subtotal_ex_caution ?? 0);
  const o = Number(booking?.caution_fee_original ?? 0);
  const r = Number(booking?.caution_fee_refunded ?? 0);
  const f = Number(booking?.caution_fee_deposit_forfeited ?? 0);
  const e = Number(booking?.caution_fee_damage_extra ?? 0);
  const t = Number(booking?.total_price ?? 0);
  const remainder = Math.max(0, o - r - f);
  const cautionDisplay = remainder + e;
  let tone = 'none';
  if (o > 0) {
    if (e > 0) tone = 'damage';
    else if (remainder < 1) {
      if (r > 0 && f < 1) tone = 'refunded_clear';
      else if (f > 0) tone = 'deposit_kept';
      else tone = 'settled';
    } else tone = 'held';
  }
  return {
    stay_subtotal: s,
    caution_original: o,
    caution_refunded: r,
    caution_deposit_forfeited: f,
    caution_damage_extra: e,
    caution_remainder: remainder,
    caution_display: cautionDisplay,
    total: t,
    tone,
  };
}

/** CSS class names for caution amount cells (prefix with fd- or dashboard). */
export function cautionToneClassName(tone, prefix = 'booking-caution-tone') {
  const base = prefix;
  if (tone === 'refunded_clear' || tone === 'settled') return `${base} ${base}--green`;
  if (tone === 'damage' || tone === 'deposit_kept') return `${base} ${base}--red`;
  if (tone === 'held') return `${base} ${base}--amber`;
  return `${base} ${base}--muted`;
}

export function ownerCanAdjustCautionFees(b) {
  if (!b || b.guest_refunded_at) return false;
  if (b.status === 'cancelled') return false;
  if (b.payment_status === 'paid') return true;
  const cash = String(b.payment_method || '').toLowerCase() === 'cash';
  return cash && ['confirmed', 'checked_in', 'checked_out'].includes(b.status);
}

export function bookingHasCautionLine(b) {
  const pb = getPricingBreakdown(b);
  return pb.caution_original > 0;
}

/** Matches API {@see App\Support\BookingCautionFee::isCautionLine} for quote surcharge rows. */
export function isCautionSurchargeLine(line) {
  if (!line || typeof line !== 'object') return false;
  const type = String(line.type_name ?? '').toLowerCase();
  const name = String(line.name ?? '').toLowerCase();
  return (
    type.includes('caution') ||
    name.includes('caution') ||
    name.includes('security deposit') ||
    name.includes('refundable deposit')
  );
}

export function sumCautionFromQuoteLines(lines) {
  if (!Array.isArray(lines)) return 0;
  let s = 0;
  for (const line of lines) {
    if (line && isCautionSurchargeLine(line)) {
      s += Number(line.amount) || 0;
    }
  }
  return s;
}

/**
 * Preview front-desk create pricing with optional discount/surcharge line overrides (mirrors API logic).
 * @param {Record<string, unknown>|null|undefined} quote from apartment quote endpoint
 * @param {{ discountAmountOverride?: number|null, surchargeAmountOverrides?: Record<string|number, number|null|undefined> }} opts
 * @returns {{ ok: true, subtotal: number, discount_amount: number, discount_percent: number|null, discount_name: string|null, stay_total_after_discount: number, surcharge_lines: unknown[], surcharge_total: number, total: number, applied_discount: { name: string, amount: number }|null } | { ok: false, message: string }}
 */
export function applyFrontDeskCreatePricingOverrides(quote, opts = {}) {
  if (!quote || typeof quote !== 'object') {
    return { ok: false, message: 'No quote' };
  }
  const subtotal = Number(quote.subtotal) || 0;
  const rawLines = Array.isArray(quote.surcharge_lines) ? quote.surcharge_lines : [];
  let lines = rawLines.map((row) => ({ ...row }));

  const lineById = new Map();
  for (let i = 0; i < lines.length; i += 1) {
    const id = Number(lines[i]?.id);
    if (Number.isFinite(id) && id > 0) lineById.set(id, i);
  }
  const surchargeAmountOverrides =
    opts.surchargeAmountOverrides && typeof opts.surchargeAmountOverrides === 'object'
      ? opts.surchargeAmountOverrides
      : {};
  for (const [rawId, rawAmount] of Object.entries(surchargeAmountOverrides)) {
    if (rawAmount == null || rawAmount === '') continue;
    const sid = Number(rawId);
    const amount = Number(rawAmount);
    if (!Number.isFinite(sid) || sid < 1 || !lineById.has(sid) || !Number.isFinite(amount) || amount < 0) {
      return { ok: false, message: 'One or more surcharge overrides are invalid for this stay.' };
    }
    const idx = lineById.get(sid);
    lines[idx] = { ...lines[idx], amount: Math.round(amount) };
  }

  let surchargeTotal = 0;
  for (const row of lines) {
    surchargeTotal += Number(row.amount) || 0;
  }

  let discountAmount = Number(quote.discount_amount) || 0;
  let discountPercent =
    quote.discount_percent === undefined || quote.discount_percent === null
      ? null
      : Number(quote.discount_percent);
  let discountName = quote.discount_name != null ? String(quote.discount_name) : null;
  const discountId = quote.discount_id != null ? quote.discount_id : null;

  const discOv =
    opts.discountAmountOverride != null &&
    opts.discountAmountOverride !== '' &&
    Number.isFinite(Number(opts.discountAmountOverride))
      ? Math.max(0, Math.round(Number(opts.discountAmountOverride)))
      : null;

  if (discOv !== null) {
    discountAmount = Math.min(subtotal, discOv);
    discountPercent = null;
    discountName = discountName ? `${discountName} (adjusted)` : 'Adjusted discount';
  }

  const stayAfter = Math.max(0, subtotal - discountAmount);
  const total = stayAfter + surchargeTotal;

  let appliedDiscount = quote.applied_discount && typeof quote.applied_discount === 'object' ? quote.applied_discount : null;
  if (discOv !== null && discountAmount > 0) {
    appliedDiscount = {
      name: discountName || 'Discount',
      amount: discountAmount,
      id: discountId,
    };
  } else if (discOv !== null && discountAmount < 1) {
    appliedDiscount = null;
  }

  return {
    ok: true,
    subtotal,
    discount_amount: discountAmount,
    discount_percent: discountPercent,
    discount_name: discountName,
    stay_total_after_discount: stayAfter,
    surcharge_lines: lines,
    surcharge_total: surchargeTotal,
    total,
    applied_discount: appliedDiscount,
  };
}
